import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import { useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { AuthProvider } from "./context/AuthContext";
import { ExportSuccessProvider } from "./context/ExportSuccessContext";
import { PermissionsProvider, usePermissions } from "./context/PermissionsContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CompanySettingsProvider } from "./context/CompanySettingsContext";
import { PublicProvider } from "./context/PublicContext";
import { HelmetProvider } from "react-helmet-async";
import NotificationBootstrap from "./components/NotificationBootstrap";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
// import OwnerLoginPage from "./pages/OwnerLoginPage";
// import SalesExecutiveLoginPage from "./pages/SalesExecutiveLoginPage";
import SuperAdminLoginPage from "./pages/SuperAdminLoginPage";
import LeadsManagementPage from "./pages/SuperAdmin/LeadsManagementPage";
import ContactLeads from "./pages/SuperAdmin/ContactLeads";
import DashboardPage from "./pages/DashboardPage";
import DashboardDesignPage from "./pages/DashboardDesignPage";
import POSBillingPage from "./pages/POSBillingPage";
import POSPaymentPage from "./pages/POSPaymentPage";
import InvoiceSuccessPage from "./pages/InvoiceSuccessPage";
import InvoiceHistoryPage from "./pages/InvoiceHistoryPage";
import LoyaltyManagementPage from "./pages/LoyaltyManagementPage";
import InventoryPage from "./pages/InventoryPage";
import CustomersPage from "./pages/CustomersPage";
import ReportsPage from "./pages/ReportsPage";
import SellerReportsPage from "./pages/SellerReportsPage";
import StockReportsPage from "./pages/StockReportsPage";
import TransitReportsPage from "./pages/TransitReportsPage";
import TaxReportsPage from "./pages/TaxReportsPage";
import SalesReportPage from "./pages/SalesReportPage";
import ItemwiseSalesReportPage from "./pages/ItemwiseSalesReportPage";
import ProfileSettings from "./pages/ProfileSettings";
import TeamAccessPage from "./pages/TeamAccessPage";
import CategoryManagementPage from "./pages/CategoryManagementPage";
import DiscountPage from "./pages/DiscountPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import AllEmployees from "./pages/Employees/AllEmployees";
import AddEmployee from "./pages/Employees/AddEmployee";
import EmployeeTracking from "./pages/Employees/EmployeeTracking";
import AttendanceReports from "./pages/Employees/AttendanceReports";
import LeaveApprovals from "./pages/Employees/LeaveApprovals";
import PayrollManagement from "./pages/Employees/PayrollManagement";

import SalesExecutiveDashboard from "./pages/SalesExecutive/SalesExecutiveDashboard";
import AttendancePage from "./pages/SalesExecutive/AttendancePage";
import CollectionsPage from "./pages/SalesExecutive/CollectionsPage";
import LeaveRequestsPage from "./pages/SalesExecutive/LeaveRequestsPage";
import IncentivesPayrollPage from "./pages/SalesExecutive/IncentivesPayrollPage";
import OwnerPurchaseRequestsPage from "./pages/OwnerPurchaseRequestsPage";
import SmartPurchaseBillPage from "./pages/SmartPurchaseBillPage";

// Super Admin Owner Reports
import OwnerInvoiceReport from "./pages/SuperAdmin/OwnerInvoiceReport";
import OwnerInventoryReport from "./pages/SuperAdmin/OwnerInventoryReport";
import OwnerCustomerReport from "./pages/SuperAdmin/OwnerCustomerReport";
import OwnerEmployeeReport from "./pages/SuperAdmin/OwnerEmployeeReport";

// import OwnerSubscriptionManagement from "./pages/OwnerSubscriptionManagement";
import SubscriptionPage from "./components/Owner/SubscriptionPage";
import PaymentPage from "./components/Owner/PaymentPage";
import OwnerSubscriptionPurchase from "./pages/OwnerSubscriptionPurchase";
import RolesPermissions from "./pages/RolesPermissions";
import SuperAdminPage from "./pages/SuperAdminPage";
import OwnerDashboardPage from "./pages/OwnerDashboardPage";
import OwnerSupportPage from "./pages/OwnerSupportPage";
import NotificationPage from "./pages/NotificationPage";
import SalesExecutivePage from "./pages/SalesExecutivePage";
import SupplierManagementPage from "./pages/SupplierManagementPage";
import StockInwardPage from "./pages/StockInwardPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import SubscriptionFailurePage from "./pages/SubscriptionFailurePage";
import PaymentProcessingPage from "./pages/PaymentProcessingPage";
import AccountRequestsPage from "./pages/AccountRequestsPage";
import Layout from "./components/Layout";
// Public Website Imports
import PublicLayout from "./components/public/PublicLayout";
import HomePage from "./pages/public/HomePage";
import ServicesPage from "./pages/public/ServicesPage";
import FeaturesPage from "./pages/public/FeaturesPage";
import PricingPage from "./pages/public/PricingPage";
import AboutPage from "./pages/public/AboutPage";
import ContactPage from "./pages/public/ContactPage";
import PrivacyPage from "./pages/public/PrivacyPage";
import TermsPage from "./pages/public/TermsPage";
import HelpCenterPage from "./pages/public/HelpCenterPage";
import PublicInvoiceView from "./pages/public/PublicInvoiceView";

const RootRoute = () => {
  const { user, userRole } = useAuth();
  const { hasPermission } = usePermissions();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user?.is_super_admin) {
    return <Navigate to="/super-admin" replace />;
  }

  if (userRole === "SALES_EXECUTIVE") {
    return <Navigate to="/sales-executive/attendance" replace />;
  }

  if (userRole === "OWNER") {
    return <Navigate to="/owner/dashboard" replace />;
  }

  // Staff/Other roles dynamic permission check to route to first allowed module
  if (hasPermission(userRole, "view_employee_dashboard")) {
    return <Navigate to="/dashboard" replace />;
  }
  if (hasPermission(userRole, "view_billing")) {
    return <Navigate to="/pos" replace />;
  }
  if (hasPermission(userRole, "view_inventory")) {
    return <Navigate to="/inventory" replace />;
  }
  if (hasPermission(userRole, "view_customers")) {
    return <Navigate to="/customers" replace />;
  }
  if (hasPermission(userRole, "view_reports")) {
    return <Navigate to="/reports" replace />;
  }
  if (hasPermission(userRole, "view_settings")) {
    return <Navigate to="/settings" replace />;
  }

  // Fallback to notifications if no module permission matches
  return <Navigate to="/notifications" replace />;
};

export default function App() {
  React.useEffect(() => {
    const APP_VERSION = 'v1.0.8';
    // Version check logic simplified - do not clear storage automatically
    localStorage.setItem('app_version', APP_VERSION);
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <PermissionsProvider>
                <SubscriptionProvider>
                  <CompanySettingsProvider>
                    <PublicProvider>
                      <ExportSuccessProvider>
                        <NotificationBootstrap />
                        <Toaster position="top-right" />
                        <Routes>
                          {/* Highest Priority Public Routes */}
                          <Route path="/public/invoice/*" element={<PublicInvoiceView />} />

                          {/* Public Website Routes */}
                          <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
                          <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
                          <Route path="/features" element={<PublicLayout><FeaturesPage /></PublicLayout>} />
                          <Route path="/pricing" element={<PublicLayout><PricingPage /></PublicLayout>} />
                          <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
                          <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
                          <Route path="/privacy-policy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
                          <Route path="/terms-of-service" element={<PublicLayout><TermsPage /></PublicLayout>} />
                          <Route path="/help-center" element={<PublicLayout><HelpCenterPage /></PublicLayout>} />

                          {/* Application Login Routes */}
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/super-admin-login" element={<SuperAdminLoginPage />} />
                          <Route path="/super-admin/leads" element={<ProtectedRoute element={<LeadsManagementPage />} requiredRole="SUPERADMIN" />} />
                          <Route path="/super-admin/contact-leads" element={<ProtectedRoute element={<ContactLeads />} requiredRole="SUPERADMIN" />} />
                          <Route path="/super-admin" element={<ProtectedRoute element={<SuperAdminPage />} requiredPermission="view_employees" />} />
                          <Route path="/super-admin/owner/:id/invoices" element={<ProtectedRoute element={<OwnerInvoiceReport />} requiredPermission="view_employees" />} />
                          <Route path="/super-admin/owner/:id/inventory" element={<ProtectedRoute element={<OwnerInventoryReport />} requiredPermission="view_employees" />} />
                          <Route path="/super-admin/owner/:id/customers" element={<ProtectedRoute element={<OwnerCustomerReport />} requiredPermission="view_employees" />} />
                          <Route path="/super-admin/owner/:id/employees" element={<ProtectedRoute element={<OwnerEmployeeReport />} requiredPermission="view_employees" />} />
                          <Route path="/owner/dashboard" element={<ProtectedRoute element={<OwnerDashboardPage />} requiredRole="OWNER" requiredPermission="view_owner_dashboard" />} />

                          {/* POS Routes - Standalone (No Sidebar) */}
                          <Route path="/pos" element={<ProtectedRoute element={<POSBillingPage />} requiredPermission="view_billing" />} />
                          <Route path="/pos-billing" element={<ProtectedRoute element={<POSBillingPage />} requiredPermission="view_billing" />} />
                          <Route path="/pos/payment" element={<ProtectedRoute element={<POSPaymentPage />} requiredPermission="view_billing" />} />
                          <Route path="/pos/invoice-success" element={<ProtectedRoute element={<InvoiceSuccessPage />} requiredPermission="view_billing" />} />


                          {/* Protected Application Routes */}
                          <Route
                            path="/*"
                            element={
                              <ProtectedRoute
                                element={
                                  <Layout>
                                    <Routes>
                                      {/* Redirect helper moved to /app */}
                                      <Route path="/app" element={<RootRoute />} />
                                      <Route path="/dashboard" element={<ProtectedRoute element={<DashboardPage />} requiredPermission="view_employee_dashboard" />} />
                                      <Route path="/dashboard-design" element={<ProtectedRoute element={<DashboardDesignPage />} requiredPermission="view_employee_dashboard" />} />
                                      <Route path="/invoices" element={<ProtectedRoute element={<InvoiceHistoryPage />} requiredPermission="export_billing" />} />
                                      <Route path="/invoice-history" element={<ProtectedRoute element={<InvoiceHistoryPage />} requiredPermission="export_billing" />} />
                                      <Route path="/loyalty" element={<ProtectedRoute element={<LoyaltyManagementPage />} requiredPermission="view_customers" />} />
                                      <Route path="/loyalty-management" element={<ProtectedRoute element={<LoyaltyManagementPage />} requiredPermission="view_loyalty" />} />
                                      <Route path="/inventory" element={<ProtectedRoute element={<InventoryPage />} requiredPermission="view_inventory" />} />
                                      <Route path="/smart-purchase" element={<ProtectedRoute element={<SmartPurchaseBillPage />} requiredPermission="create_purchase" />} />
                                      <Route path="/suppliers" element={<ProtectedRoute element={<SupplierManagementPage />} requiredPermission="view_vendors" />} />
                                      <Route path="/stock-inward" element={<ProtectedRoute element={<StockInwardPage />} requiredPermission="create_inventory" />} />
                                      <Route path="/customers" element={<ProtectedRoute element={<CustomersPage />} requiredPermission="view_customers" />} />
                                      <Route path="/reports" element={<ProtectedRoute element={<ReportsPage />} requiredPermission="view_reports" />} />

                                      <Route path="/settings" element={<ProtectedRoute element={<ProfileSettings />} requiredPermission="view_settings" />} />
              
                                      {/* Employees Module */}
                                      <Route path="/employees" element={<ProtectedRoute element={<AllEmployees />} requiredPermission="view_employees" />} />
                                      <Route path="/employees/add" element={<ProtectedRoute element={<AddEmployee />} requiredPermission="create_employees" />} />
                                      <Route path="/employees/tracking" element={<ProtectedRoute element={<EmployeeTracking />} requiredPermission="view_sales" />} />
                                      <Route path="/employees/attendance" element={<ProtectedRoute element={<AttendanceReports />} requiredPermission="view_attendance" />} />
                                      <Route path="/employees/leaves" element={<ProtectedRoute element={<LeaveApprovals />} requiredPermission="approve_attendance" />} />
                                      <Route path="/employees/payroll" element={<ProtectedRoute element={<PayrollManagement />} requiredPermission="view_payroll" />} />

                                      {/* Sales Executive Module */}
                                      <Route path="/sales-executive" element={<Navigate to="/sales-executive/attendance" replace />} />
                                      <Route path="/sales-executive/dashboard" element={<ProtectedRoute element={<SalesExecutiveDashboard />} requiredRole="SALES_EXECUTIVE" />} />
                                      <Route path="/sales-executive/attendance" element={<ProtectedRoute element={<AttendancePage />} requiredRole="SALES_EXECUTIVE" />} />
                                      <Route path="/sales-executive/collections" element={<ProtectedRoute element={<CollectionsPage />} requiredRole="SALES_EXECUTIVE" />} />
                                      <Route path="/sales-executive/leaves" element={<ProtectedRoute element={<LeaveRequestsPage />} requiredRole="SALES_EXECUTIVE" />} />
                                      <Route path="/sales-executive/incentives-payroll" element={<ProtectedRoute element={<IncentivesPayrollPage />} requiredRole="SALES_EXECUTIVE" />} />

                                      <Route path="/notifications" element={<ProtectedRoute element={<NotificationPage />} />} />
                                      <Route path="/company-profile" element={<ProtectedRoute element={<CompanyProfilePage />} requiredPermission="view_settings" />} />
                                      <Route path="/owner/roles-permissions" element={<ProtectedRoute element={<RolesPermissions />} requiredPermission="view_employees" />} />
                                      <Route path="/owner/subscription-management" element={<ProtectedRoute element={<SubscriptionPage />} requiredPermission="view_payments" />} />
                                      <Route path="/owner/payment" element={<ProtectedRoute element={<PaymentPage />} requiredPermission="view_payments" />} />
                                      <Route path="/owner/payment-success" element={<ProtectedRoute element={<SubscriptionSuccessPage />} requiredPermission="view_payments" />} />
                                      <Route path="/owner/payment-failure" element={<ProtectedRoute element={<SubscriptionFailurePage />} requiredPermission="view_payments" />} />
                                      <Route path="/owner/payment-processing" element={<ProtectedRoute element={<PaymentProcessingPage />} requiredPermission="view_payments" />} />
                                      <Route path="/support" element={<ProtectedRoute element={<OwnerSupportPage />} requiredPermission="view_services" />} />
                                      <Route path="/owner/categories" element={<ProtectedRoute element={<CategoryManagementPage />} requiredPermission="edit_inventory" />} />
                                      <Route path="/owner/purchase-requests" element={<ProtectedRoute element={<OwnerPurchaseRequestsPage />} requiredPermission="view_purchase" />} />
                                      <Route path="/discounts" element={<ProtectedRoute element={<DiscountPage />} requiredRole="OWNER" />} />
                                      <Route path="/account-requests" element={<ProtectedRoute element={<AccountRequestsPage />} requiredRole="SUPERADMIN" />} />
                                    </Routes>
                                  </Layout>
                                }
                              />
                            }
                          />
                        </Routes>
                      </ExportSuccessProvider>
                    </PublicProvider>
                  </CompanySettingsProvider>
                </SubscriptionProvider>
              </PermissionsProvider>
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
